import React, { useState, useEffect, useRef } from "react";
import {
  deleteImage,
  updateImageAlbum,
  updateImageCompression,
  updateImageDetails,
} from "./api/imageApi";
import { useGlobalContext } from "../Context/Context";

// CSS
import "./imageview.css";

// Images
import Close from "./picture/close.png";
import Size from "./picture/size.png";
import Edit from "./picture/edit.png";
import Info from "./picture/info.png";
import Album from "./picture/album.png";
import Back from "./picture/back.png";
import Plane from "./picture/plane.png";
import Image from "./picture/img.png";
import AlbumIcon from "./picture/album.png"; // Icon for albums in the selection

// Components
import Button from "../Button/Button";

const ImageView = ({
  image,
  onClose,
  onDelete,
  onUpdate,
  albums = [],
  onAddToAlbum,
}) => {
  const [isDesc, setIsDesc] = useState(false); // Toggle between image and description
  const [isEditing, setIsEditing] = useState(false); // Editing mode for title/description
  const [showSizeButtons, setShowSizeButtons] = useState(false); // Toggle size buttons
  const [showAlbumSelector, setShowAlbumSelector] = useState(false); // Toggle album selector
  const [selectedCompression, setSelectedCompression] = useState(100); // Default compression level
  const [editedTitle, setEditedTitle] = useState(image?.title || ""); // Editable title
  const [editedDescription, setEditedDescription] = useState(
    image?.description || ""
  ); // Editable description

  const { galleryUrl } = useGlobalContext();

  useEffect(() => {
    setEditedTitle(image?.title || "");
    setEditedDescription(image?.description || "");
    setSelectedCompression(parseInt(image?.compression_level) || 100);
  }, [image]);

  const handleSizeToggle = () => setShowSizeButtons(!showSizeButtons);

  const handleChangeDescState = () => setIsDesc(!isDesc);

  const handleSetEditState = () => setIsEditing(true);

  const handleDelete = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this image?"
    );
    if (!confirmDelete) return;

    try {
      await deleteImage(image.image_id); // Call the delete API
      onDelete(image.image_id); // Notify the parent component to update state
      alert("Image deleted successfully!");
      onClose(); // Immediately navigate back to the gallery
    } catch (error) {
      alert("Failed to delete the image.");
      onClose(); // Navigate back to the gallery anyway
    }
  };

  const handleSaveEdit = async () => {
    try {
      const response = await updateImageDetails(
        image.image_id,
        editedTitle,
        editedDescription
      );

      if (response.status === 200) {
        alert("Image details updated successfully.");
        onUpdate(image.image_id, editedTitle, editedDescription); // Update parent state if needed
        setIsEditing(false);
      } else {
        alert(response.message || "Failed to update image details.");
      }
    } catch (error) {
      alert("An error occurred while updating image details.");
    }
  };

  const handleCompressionUpdate = async (compressionLevel) => {
    try {
      // Update the backend compression level
      const response = await updateImageCompression(
        image.image_id,
        compressionLevel
      );

      if (response.status === 200) {
        // Update the local state to reflect the selected compression
        setSelectedCompression(compressionLevel);
        alert(`Compression level updated to ${compressionLevel}%`);
      } else {
        alert(`Failed to update compression level: ${response.message}`);
      }
    } catch (error) {
      alert("An error occurred while updating compression level.");
    }
  };

  const handleCompressionSelect = async (percentage) => {
    setSelectedCompression(percentage);

    // Simulate saving compression and reverting back
    try {
      const response = await fetch(`/api/compressImage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          image_id: image.image_id,
          compression_level: percentage,
        }),
      });

      const result = await response.json();
      if (response.ok) {
        alert(`Compression set to ${percentage}% for ${image.title}`);
      } else {
        throw new Error(result.message || "Failed to compress image.");
      }
    } catch (error) {
      console.error(error);
      alert("Failed to compress image.");
    }

    setTimeout(() => setShowSizeButtons(false), 1000);
  };

  const handleAlbumSelection = async (albumId) => {
    try {
      await updateImageAlbum(image.image_id, albumId); // Call API to update album_id
      onAddToAlbum(image.image_id, albumId); // Update frontend state
      alert(`Image moved to the selected album!`);
      setShowAlbumSelector(false); // Close album selector
      onClose(); // Return to gallery
    } catch (error) {
      alert("Failed to move the image to the selected album.");
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  };

  return (
    <div className="image-view-wrapper">
      {/* Top Section */}
      <div className="image-view-top">
        {!isEditing ? (
          <>
            <span>{editedTitle}</span>
            <div className="btn-wrapper-div">
              <img src={Edit} alt="Edit" onClick={handleSetEditState} />
            </div>
          </>
        ) : (
          <>
            <input
              type="text"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              placeholder="Edit Title"
            />
            <button>
              <img src={Plane} alt="Save" onClick={handleSaveEdit} />
            </button>
          </>
        )}
      </div>

      {/* Middle Section */}
      <div className="image-view-mid">
        {!isDesc ? (
          <img src={galleryUrl + image.src} alt={image.title} />
        ) : (
          <>
            {!isEditing ? (
              <p>{editedDescription}</p>
            ) : (
              <textarea
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                placeholder="Edit Description"
                rows={20}
              />
            )}
          </>
        )}
      </div>

      {/* Bottom Section */}
      <div className="image-view-bot" style={{}}>
        {!showSizeButtons && !showAlbumSelector ? (
          <div className="image-view-bot-row1">
            <div className="btn-wrapper-div">
              {!isDesc ? (
                <img
                  src={Info}
                  alt="Show Info"
                  onClick={handleChangeDescState}
                />
              ) : (
                <img
                  src={Image}
                  alt="Show Image"
                  onClick={handleChangeDescState}
                />
              )}
            </div>
            <div className="btn-wrapper-div">
              <img
                src={Size}
                alt="Toggle Size Options"
                onClick={handleSizeToggle}
              />
            </div>
            <div className="btn-wrapper-div">
              <img
                src={Album}
                alt="Change Album"
                onClick={() => setShowAlbumSelector(true)}
              />
            </div>
            <div className="btn-wrapper-div">
              <img src={Close} alt="Delete" onClick={handleDelete} />
            </div>
          </div>
        ) : showSizeButtons ? (
          <div className="image-view-bot-row1">
            {[100, 50, 30, 10].map((percentage) => (
              <div
                className="btn-wrapper-div"
                key={percentage}
                style={{
                  backgroundColor:
                    selectedCompression === percentage ? "green" : "white",
                  color: selectedCompression === percentage ? "white" : "black",
                  border:
                    selectedCompression === percentage
                      ? "2px solid black"
                      : "1px solid gray",
                  transition: "background-color 0.2s, color 0.2s, border 0.2s", // Smooth UI updates
                }}
              >
                <span
                  onClick={() => handleCompressionUpdate(percentage)}
                  style={{
                    cursor: "pointer",
                    display: "block",
                    width: "100%",
                    height: "100%",
                    textAlign: "center",
                    lineHeight: "1.5",
                    fontWeight: "bold",
                    marginTop: "61%",
                    fontSize: "15px",
                  }}
                >
                  {percentage}%
                </span>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="album-selector">
              <div className="grid">
                {albums.map((album) => (
                  <div
                    key={album.id}
                    className="grid-item"
                    onClick={() => handleAlbumSelection(album.id)}
                  >
                    <img src={AlbumIcon} alt={album.name} />
                    <p>{album.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        <div className="image-view-bot-row2">
          <div>
            <h5>{formatDate(image.created_at)}</h5>
            <h5 className="add-flow-size">{image.file_size}</h5>
          </div>
          <button className="btn-wrapper-div"
            onClick={
              !showAlbumSelector ? onClose : () => setShowAlbumSelector(false)
            }
          >
            <img src={Back} alt="Cancel" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageView;
